import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { ConfigProvider } from "antd";
import arEG from "antd/locale/ar_EG";
import enUS from "antd/locale/en_US";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { DashboardAntDatePicker } from "src/components/reusable/AntdDatePicker/DashboardAntDatePicker";
import { getNumberLocale } from "src/utils/numberFormat";
import "./DateRangePicker.scss";

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onRangeChange: (_range: { start: string; end: string }) => void;
  maxPastMonths?: number;
}

const DateRangePicker: FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onRangeChange,
}) => {
  const { t, i18n } = useTranslation();
  const isAr = (i18n.resolvedLanguage ?? i18n.language ?? "en")
    .toLowerCase()
    .startsWith("ar");
  const antdLocale = isAr ? arEG : enUS;
  const locale = useMemo(() => getNumberLocale(i18n.language), [i18n.language]);

  const [localStart, setLocalStart] = useState<Dayjs | null>(
    startDate ? dayjs(startDate, "YYYY-MM-DD") : null
  );
  const [localEnd, setLocalEnd] = useState<Dayjs | null>(
    endDate ? dayjs(endDate, "YYYY-MM-DD") : null
  );

  useEffect(() => {
    setLocalStart(startDate ? dayjs(startDate, "YYYY-MM-DD") : null);
    setLocalEnd(endDate ? dayjs(endDate, "YYYY-MM-DD") : null);
  }, [startDate, endDate]);

  const handleStartChange = (date: Dayjs | null) => {
    setLocalStart(date);
  };

  const handleEndChange = (date: Dayjs | null) => {
    setLocalEnd(date);
    if (localStart && date) {
      onRangeChange({
        start: localStart.format("YYYY-MM-DD"),
        end: date.format("YYYY-MM-DD"),
      });
    }
  };

  const formatDisplayDate = useCallback(
    (date: Dayjs) => {
      if (isAr) {
        return new Intl.DateTimeFormat(locale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(date.toDate());
      }
      return date.format("DD-MM-YYYY");
    },
    [isAr, locale]
  );

  return (
    <ConfigProvider locale={antdLocale} direction={isAr ? "rtl" : "ltr"}>
      <div className="date-range-picker-container">
        <DashboardAntDatePicker
          value={localStart}
          format={isAr ? formatDisplayDate : "DD-MM-YYYY"}
          onChange={handleStartChange}
          placeholder={t("organicMedia.datePicker.startDate")}
          className="dashboard-date-picker"
          placement={isAr ? "bottomRight" : "bottomLeft"}
          disabledDate={() => false}
        />

        <DashboardAntDatePicker
          value={localEnd}
          format={isAr ? formatDisplayDate : "DD-MM-YYYY"}
          onChange={handleEndChange}
          placeholder={t("organicMedia.datePicker.endDate")}
          className="dashboard-date-picker"
          placement={isAr ? "bottomRight" : "bottomLeft"}
          disabledDate={date =>
            localStart ? date.isBefore(localStart, "day") : false
          }
        />
      </div>
    </ConfigProvider>
  );
};

export default DateRangePicker;
